import { createTheme, alpha } from "@mui/material/styles";
import { grey } from "@mui/material/colors";

import Paper, { PaperProps } from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useState, useRef } from "react";

const DraggablePaperComponent = (props: PaperProps) => {
  const canDrag = useMediaQuery("(min-width: 900px) and (pointer: fine)");
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const offset = useRef({ x: 0, y: 0 });
  const paperRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canDrag) return;
    const title = (e.target as HTMLElement).closest(".MuiDialogTitle-root");
    if (!title) return;
    const rect = paperRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({ x: rect.left, y: rect.top });
      offset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    } else {
      offset.current = { x: 0, y: 0 };
    }
    setHasDragged(true);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    setPosition({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  return (
    <Paper
      {...props}
      ref={paperRef}
      onMouseDown={handleMouseDown}
      style={{
        ...props.style,
        ...(hasDragged &&
          canDrag && {
            position: "absolute",
            margin: 0,
            left: position.x,
            top: position.y,
          }),
      }}
    />
  );
};

const theme = createTheme({
  palette: {
    primary: {
      main: "#1E40AF",
      light: "#3B82F6",
      dark: "#1E3A8A",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#334155",
      light: "#475569",
      dark: "#1E293B",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#B91C1C",
      light: "#DC2626",
      dark: "#991B1B",
    },
    warning: {
      main: "#B45309",
      light: "#D97706",
      dark: "#92400E",
    },
    success: {
      main: "#047857",
      light: "#059669",
      dark: "#065F46",
    },
    info: {
      main: "#0369A1",
      light: "#0284C7",
      dark: "#075985",
    },
    background: {
      default: "#F1F5F9",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#0F172A",
      secondary: "#334155",
      disabled: "#64748B",
    },
    divider: "#CBD5E1",
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    h1: {
      fontSize: "2.25rem",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.025em",
      color: "#0F172A",
    },
    h2: {
      fontSize: "1.875rem",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.025em",
      color: "#0F172A",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: "-0.025em",
      color: "#0F172A",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#0F172A",
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#0F172A",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.4,
      color: "#0F172A",
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
      color: "#334155",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.5,
      color: "#334155",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.5,
      color: "#334155",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.5,
      color: "#334155",
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
      fontSize: "0.875rem",
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.5,
      color: "#64748B",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#E2E8F0",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#94A3B8",
            borderRadius: "3px",
          },
          "& .MuiDataGrid-root": {
            border: "none",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow:
              "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${grey[200]}`,
            fontSize: "0.875rem",
            color: "#334155",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#F8FAFC",
            borderBottom: `1px solid ${grey[300]}`,
            minHeight: "48px !important",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "0 16px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "#0F172A",
          },
          "& .MuiDataGrid-row": {
            "&:not(.MuiDataGrid-row--clickable)": {
              cursor: "default",
              "&:hover": {
                backgroundColor: "transparent",
              },
            },
            "&.MuiDataGrid-row--clickable": {
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: alpha("#1E40AF", 0.04),
              },
            },
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: alpha("#1E40AF", 0.08),
            "&:hover": {
              backgroundColor: alpha("#1E40AF", 0.12),
            },
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: "#FFFFFF",
          color: "#0F172A",
          borderRadius: 8,
          border: `1px solid ${grey[200]}`,
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          padding: "8px 10px",
          fontSize: "0.8125rem",
        },
        arrow: {
          color: "#FFFFFF",
          boxShadow: "0 0 0 1px #E5E7EB inset, 0 1px 2px 0 rgb(0 0 0 / 0.05)",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        PaperComponent: DraggablePaperComponent,
      },
      styleOverrides: {
        paper: {
          "@media (max-width: 899.95px)": {
            margin: 16,
            maxWidth: "calc(100% - 32px) !important",
            maxHeight: "calc(100% - 32px)",
            minWidth: "0 !important",
          },
        },
        paperFullScreen: {
          "@media (max-width: 899.95px)": {
            margin: 0,
            maxWidth: "100% !important",
            maxHeight: "100%",
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { minWidth: 0, "@media (max-width: 599.95px)": { padding: 16 } },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          "@media (max-width: 899.95px)": {
            flexWrap: "wrap",
            gap: 8,
            "& > :not(:first-of-type)": { marginLeft: 0 },
          },
          "@media (max-width: 599.95px)": { padding: 16 },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          cursor: "move",
          "@media (max-width: 899.95px), (pointer: coarse)": {
            cursor: "default",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
        elevation1: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
        elevation2: {
          boxShadow:
            "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        },
        elevation3: {
          boxShadow:
            "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          padding: "8px 16px",
          fontWeight: 500,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        contained: {
          boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
        containedPrimary: {
          backgroundColor: "#1E40AF",
          "&:hover": {
            backgroundColor: "#1E3A8A",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "6px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: alpha(grey[50], 0.5),
            },
            "&.Mui-focused": {
              backgroundColor: "#FFFFFF",
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#F8FAFC",
          fontWeight: 600,
          color: "#0F172A",
          padding: "12px 16px",
          fontSize: "0.875rem",
        },
        root: {
          borderBottom: `1px solid ${grey[200]}`,
          padding: "12px 16px",
          fontSize: "0.875rem",
          color: "#334155",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          overscrollBehaviorX: "contain",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontWeight: 500,
          fontSize: "0.75rem",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: "8px",
        },
        switchBase: {
          padding: "7px",
          "&.Mui-checked": {
            transform: "translateX(24px)",
            "& + .MuiSwitch-track": {
              backgroundColor: "#1E40AF",
              opacity: 1,
            },
            "& .MuiSwitch-thumb": {
              backgroundColor: "#FFFFFF",
            },
          },
        },
        thumb: {
          width: "16px",
          height: "16px",
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          backgroundColor: "#FFFFFF",
          marginTop: "2px",
        },
        track: {
          borderRadius: "10px",
          backgroundColor: grey[400],
          opacity: 1,
          height: "20px",
          width: "40px",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: `1px solid ${grey[200]}`,
          borderRadius: "8px",
          "&:before": { display: "none" },
          "&.Mui-expanded": {
            margin: "8px 0",
          },
        },
      },
    },
  },
});

export default theme;
